import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { useOrg } from "@/context/OrgContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const STATUS_DOT = {
  active: "bg-[#3D8B65]",
  onboarding: "bg-[#FFB000]",
  at_risk: "bg-[#C95555]",
  paused: "bg-[#8A93A3]",
};

export function ClientSwitcher() {
  const { orgs, orgId, setOrgId, clearOrg, currentOrg } = useOrg();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const clientOrgs = orgs.filter((o) => o.status !== "archived");

  const pickClient = (id) => {
    setOrgId(id);
    setOpen(false);
    navigate("/");
  };

  const goPortfolio = () => {
    clearOrg();
    setOpen(false);
    navigate("/portfolio");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="client-switcher-trigger"
          className="flex h-10 min-w-[190px] max-w-[280px] items-center gap-2 rounded-full border border-[#E4E8EF] bg-white pl-2.5 pr-3 text-left transition-colors hover:border-[#FFC24D]"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0B1729] text-xs font-bold text-[#FFC24D]">
            {currentOrg ? currentOrg.company_name?.[0] : <Icons.LayoutGrid size={14} />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-[#1E2D44]/40">
              {currentOrg ? "Client" : "Amden"}
            </span>
            <span className="block truncate text-sm font-semibold text-[#0B1729]">
              {currentOrg ? currentOrg.company_name : "Portfolio overview"}
            </span>
          </span>
          <Icons.ChevronsUpDown size={15} className="shrink-0 text-[#1E2D44]/40" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] p-0" data-testid="client-switcher-panel">
        <Command>
          <CommandInput placeholder="Search clients…" data-testid="client-switcher-search" />
          <CommandList>
            <CommandEmpty>No clients found.</CommandEmpty>
            <CommandGroup heading="Amden">
              <CommandItem
                value="portfolio overview amden"
                onSelect={goPortfolio}
                data-testid="switch-to-portfolio"
                className="cursor-pointer gap-2.5 py-2.5"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#0B1729] text-[#FFC24D]">
                  <Icons.LayoutGrid size={14} />
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-medium text-[#0B1729]">Portfolio overview</span>
                  <span className="block text-[11px] text-[#1E2D44]/50">All clients, briefing &amp; RAG status</span>
                </span>
                {!orgId && <Icons.Check size={15} className="text-[#3D8B65]" />}
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="Clients">
              {clientOrgs.map((o) => (
                <CommandItem
                  key={o.id}
                  value={o.company_name}
                  onSelect={() => pickClient(o.id)}
                  data-testid={`switch-client-${o.id}`}
                  className="cursor-pointer gap-2.5 py-2.5"
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F4F6F9] text-xs font-bold text-[#0B1729]">
                    {o.company_name?.[0]}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[o.status] || "bg-[#8A93A3]"}`} />
                      <span className="truncate text-sm font-medium text-[#0B1729]">{o.company_name}</span>
                    </span>
                    {o.consultant_name && (
                      <span className="block truncate text-[11px] text-[#1E2D44]/50">Led by {o.consultant_name}</span>
                    )}
                  </span>
                  {orgId === o.id && <Icons.Check size={15} className="text-[#3D8B65]" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
